import axios from 'axios'

const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  timeout: 10000
})

const VISITOR_KEY = 'mall_visitor_id'

function getVisitorId() {
  if (typeof localStorage === 'undefined') return ''
  let visitorId = localStorage.getItem(VISITOR_KEY)
  if (!visitorId) {
    visitorId = `pc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
    localStorage.setItem(VISITOR_KEY, visitorId)
  }
  return visitorId
}

export function createPcTrackingEvent(prefix) {
  return `${prefix || 'event'}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

service.interceptors.request.use(config => {
  const token = typeof localStorage === 'undefined' ? '' : localStorage.getItem('mall_access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Visitor-Id'] = getVisitorId()
  config.headers['X-Client-Source'] = 'PC'
  return config
})

function unwrap(response) {
  const body = response.data || {}
  if (body.code === 200) return body.data
  throw new Error(body.msg || '接口请求失败')
}

export function getPcCategoryTree() {
  return service.get('/app/category/tree').then(unwrap)
}

export function getPcCategoryNavigation() {
  return service.get('/app/category/navigation').then(unwrap)
}

export function getPcProductList(params) {
  return service.get('/app/product/list', { params }).then(unwrap)
}

export function getPcHotProducts() {
  return service.get('/app/product/hot').then(unwrap)
}

export function getPcProductFilterOptions(params) {
  return service.get('/app/product/filter-options', { params }).then(unwrap)
}

export function getPcProductDetail(id, viewEvent) {
  return service.get(`/app/product/${id}`, { params: viewEvent ? { viewEvent } : {} }).then(unwrap)
}

export function submitPcSourcing(data) {
  return service.post('/app/sourcing', data).then(unwrap)
}

export function getPcSourcingRecords(params) {
  return service.get('/app/member/sourcing', { params }).then(unwrap)
}
export function getPcExpressOptions() {
  return service.get('/app/express/options').then(unwrap)
}
export function checkPcCartProducts(productIds) {
  return service.post('/app/product/cart-check', { productIds }).then(unwrap)
}

export function getPcMemberProfile() {
  return service.get('/app/auth/me').then(unwrap)
}

export function loginPcByPhone(data) {
  return service.post('/app/auth/phone-login', data).then(unwrap)
}

export function sendPcLoginSmsCode(data) {
  return service.post('/app/auth/sms-code', data).then(unwrap)
}

export function updatePcMemberProfile(data) {
  return service.post('/app/auth/profile', data).then(unwrap)
}

export function uploadPcBusinessLicense(file) {
  const formData = new FormData()
  formData.append('file', file)
  return service.post('/app/auth/business-license', formData, { timeout: 60000 }).then(unwrap)
}

export function getPcMemberAddresses() {
  return service.get('/app/member/addresses').then(unwrap)
}

export function savePcMemberAddress(data) {
  return service.post('/app/member/address', data).then(unwrap)
}

export function deletePcMemberAddress(id) {
  return service.delete(`/app/member/address/${id}`).then(unwrap)
}

export function getPcMemberOrders(params) {
  return service.get('/app/member/orders', { params }).then(unwrap)
}

export function getPcMemberBomRecords(params) {
  return service.get('/app/member/boms', { params }).then(unwrap)
}

export function getPcMemberBomRecord(id) {
  return service.get(`/app/member/bom/${id}`).then(unwrap)
}

export function deletePcMemberBomRecord(id) {
  return service.delete(`/app/member/bom/${id}`).then(unwrap)
}

export function cancelPcMemberOrder(orderNo) {
  return service.post(`/app/member/order/${orderNo}/cancel`).then(unwrap)
}

export function getPcMemberInvoices(params) {
  return service.get('/app/member/invoices', { params }).then(unwrap)
}

export function getPcMemberInvoiceProfiles() {
  return service.get('/app/member/invoice-profiles').then(unwrap)
}

export function savePcMemberInvoiceProfile(data) {
  return service.post('/app/member/invoice-profile', data).then(unwrap)
}

export function deletePcMemberInvoiceProfile(id) {
  return service.delete(`/app/member/invoice-profile/${id}`).then(unwrap)
}

export function savePcMemberInvoiceEmail(email) {
  return service.post('/app/member/invoice-email', { email }).then(unwrap)
}

export function uploadPcBom(file) {
  const formData = new FormData()
  formData.append('file', file)
  return service.post('/app/bom/match', formData, {
    timeout: 60000
  }).then(unwrap)
}
export function uploadPcProductSearch(file) {
  const formData = new FormData()
  formData.append('file', file)
  return service.post('/app/product/import-search', formData, { timeout: 60000 }).then(unwrap)
}

export function getPcMallSettings() {
  return service.get('/app/mall/settings').then(unwrap)
}

export function createPcOrder(data) {
  return service.post('/app/order/create', data).then(unwrap)
}

export function previewPcOrder(data) {
  return service.post('/app/order/checkout-preview', data).then(unwrap)
}

export function createPcNativePayment(data) {
  return service.post('/app/pay/wechat/native', data).then(unwrap)
}

export function createPcAlipayPayment(data) {
  return service.post('/app/pay/alipay/page', data).then(unwrap)
}

export function queryPcAlipayPayment(orderNo) {
  return service.get('/app/pay/alipay/query', { params: { orderNo } }).then(unwrap)
}

export function getPcOrderDetail(orderNo) {
  return service.get(`/app/order/${orderNo}`).then(unwrap)
}

export function downloadPcOrderContract(orderNo) {
  return service.get(`/app/order/${encodeURIComponent(orderNo)}/contract`, {
    responseType: 'blob',
    timeout: 30000
  })
}

export function downloadPcBomFile(id, type = 'source') {
  return service.get(`/app/bom/record/${id}/file`, { params: { type }, responseType: 'blob', timeout: 30000 })
}

export function downloadPcBomTemplate() {
  return service.get('/app/bom/template', { responseType: 'blob', timeout: 30000 })
}

export function getPcMemberOrderDetail(orderNo) {
  return service.get(`/app/member/order/${orderNo}`).then(unwrap)
}

export function confirmPcMemberOrder(orderNo) {
  return service.post(`/app/member/order/${orderNo}/receive`).then(unwrap)
}

export function requestPcMemberRefund(orderNo, reason) {
  return service.post(`/app/member/order/${orderNo}/refund`, { reason }).then(unwrap)
}

export function getPcAvailableCoupons() {
  return service.get('/app/coupon/available').then(unwrap)
}

export function getPcMemberCoupons(status) {
  return service.get('/app/coupon/list', { params: { status } }).then(unwrap)
}

export function receivePcCoupon(id) {
  return service.post(`/app/coupon/${id}/receive`).then(unwrap)
}

export function getPcPointsGiftList(params) {
  return service.get('/app/points/gift/list', { params }).then(unwrap)
}

export function getPcPointsGiftDetail(id) {
  return service.get(`/app/points/gift/${id}`).then(unwrap)
}

export function createPcPointsExchange(data) {
  return service.post('/app/points/exchange/create', data).then(unwrap)
}

export function getPcPointsExchangeRecords(params) {
  return service.get('/app/points/exchange/my-list', { params }).then(unwrap)
}
export function confirmPcPointsExchange(exchangeNo) {
  return service.post(`/app/points/exchange/${exchangeNo}/receive`).then(unwrap)
}
export function cancelPcPointsExchange(exchangeNo, reason) {
  return service.post(`/app/points/exchange/${exchangeNo}/cancel`, { reason }).then(unwrap)
}

export function getPcPointsRule() {
  return service.get('/app/points/rule').then(unwrap)
}

export function getPcPointsLogs(params) {
  return service.get('/app/points/log/my-list', { params }).then(unwrap)
}

export function submitPcBomManual(id, data) {
  return service.post(`/app/member/bom/${id}/manual`, data).then(unwrap)
}
export function acceptPcBomQuote(id) {
  return service.post(`/app/member/bom/${id}/accept-quote`).then(unwrap)
}

export function getPcPublicNewsList(params) {
  return service.get('/pc/news/list', { params }).then(unwrap)
}

export function getPcPublicNewsDetail(id) {
  return service.get(`/pc/news/${id}`).then(unwrap)
}

export function getPcPublicVideoList(params) {
  return service.get('/pc/video/list', { params }).then(unwrap)
}

export function getPcPublicVideoDetail(id) {
  return service.get(`/pc/video/${id}`).then(unwrap)
}
